export const PICKY_TEST_VERSION = 1;
export const PICKY_PATTERN_THRESHOLD = 0.5;
export const PICKY_PATTERN_MIN_SELECTIONS = 2;

export type PickyCategoryId =
  | "livestock"
  | "poultry"
  | "livestock-organs"
  | "poultry-organs"
  | "fish"
  | "crustaceans"
  | "shellfish"
  | "soft-seafood"
  | "freshwater"
  | "eggs"
  | "leafy-vegetables"
  | "fruiting-vegetables"
  | "flowers-mushrooms"
  | "alliums"
  | "soy-products"
  | "staples"
  | "fruit";

export type PickyPatternId =
  | "red-meat"
  | "poultry"
  | "organs"
  | "fish"
  | "crustaceans"
  | "shellfish"
  | "soft-seafood"
  | "freshwater"
  | "eggs"
  | "leafy-vegetables"
  | "fruiting-vegetables"
  | "flowers-mushrooms"
  | "alliums"
  | "soy-products"
  | "staples"
  | "fruit";

export interface PickyIngredient {
  id: string;
  name: string;
}

export interface PickyCategory {
  id: PickyCategoryId;
  name: string;
  ingredients: readonly PickyIngredient[];
}

interface PickyProfileBase {
  id: string;
  name: string;
  tags: readonly [string, string, string];
  description: string;
}

export interface PickyScoreProfile extends PickyProfileBase {
  kind: "score";
  minScore: number;
  maxScore: number;
}

export interface PickyPatternProfile extends PickyProfileBase {
  kind: "pattern";
  patternId: PickyPatternId;
  focusLabel: string;
  categoryIds: readonly PickyCategoryId[];
}

export type PickyProfile = PickyScoreProfile | PickyPatternProfile;

export interface PickyPatternScore {
  profile: PickyPatternProfile;
  selectedCount: number;
  totalCount: number;
  ratio: number;
}

export interface PickyResult {
  index: number;
  selectedCount: number;
  totalCount: number;
  profile: PickyProfile;
  primaryPattern: PickyPatternScore | null;
}

export interface PickyTestDraft {
  version: typeof PICKY_TEST_VERSION;
  selectedIngredientIds: string[];
  expandedCategoryIds: PickyCategoryId[];
}

function ingredientList(prefix: string, names: readonly string[]): readonly PickyIngredient[] {
  return names.map((name, index) => ({ id: `${prefix}-${String(index + 1).padStart(2, "0")}`, name }));
}

export const PICKY_CATEGORIES: readonly PickyCategory[] = [
  {
    id: "livestock",
    name: "畜肉类",
    ingredients: ingredientList("livestock", [
      "猪肉", "牛肉", "羊肉", "兔肉", "猪蹄（猪脚）", "猪耳朵", "猪舌头", "猪尾巴", "猪脑", "牛骨髓", "排骨", "牛蹄筋", "牛板筋",
    ]),
  },
  {
    id: "poultry",
    name: "家禽肉",
    ingredients: ingredientList("poultry", [
      "鸡肉", "鸭肉", "鹅肉", "鸽肉", "鹌鹑肉", "鸭脖", "鸭头", "鸭舌", "鸭锁骨", "鸡爪（凤爪）", "鸭爪", "鸭肠", "鸭翅（翅中、翅根）",
    ]),
  },
  {
    id: "livestock-organs",
    name: "畜内脏",
    ingredients: ingredientList("livestock-organs", [
      "猪肝", "猪肚", "猪大肠", "猪腰子（猪腰）", "猪心", "猪血", "牛肚（毛肚、百叶）", "牛杂",
    ]),
  },
  {
    id: "poultry-organs",
    name: "禽内脏",
    ingredients: ingredientList("poultry-organs", ["鸡胗", "鸡心", "鸭胗", "鹅胗", "鸡肝", "鸭肝"]),
  },
  {
    id: "fish",
    name: "鱼类",
    ingredients: ingredientList("fish", [
      "三文鱼", "金枪鱼", "鳕鱼", "鲈鱼", "带鱼", "黄鱼", "鲤鱼", "鲫鱼", "草鱼", "鲶鱼", "鱼头", "鱼杂", "鱼籽", "鱼泡", "鱼丸", "鱼皮",
    ]),
  },
  {
    id: "crustaceans",
    name: "虾蟹类",
    ingredients: ingredientList("crustaceans", ["虾", "小龙虾", "皮皮虾", "蟹", "虾滑", "蟹柳"]),
  },
  {
    id: "shellfish",
    name: "螺贝类",
    ingredients: ingredientList("shellfish", ["扇贝", "生蚝", "花甲", "蛏子", "海螺", "田螺", "螺蛳"]),
  },
  {
    id: "soft-seafood",
    name: "软体及其他水产",
    ingredients: ingredientList("soft-seafood", ["章鱼", "鱿鱼", "墨鱼", "海蜇", "鲍鱼", "海参"]),
  },
  {
    id: "freshwater",
    name: "两栖/淡水河鲜",
    ingredients: ingredientList("freshwater", ["牛蛙", "泥鳅", "黄鳝", "甲鱼"]),
  },
  {
    id: "eggs",
    name: "蛋类",
    ingredients: ingredientList("eggs", ["鸡蛋", "鸭蛋", "鹌鹑蛋", "鹅蛋"]),
  },
  {
    id: "leafy-vegetables",
    name: "叶菜",
    ingredients: ingredientList("leafy-vegetables", [
      "白菜", "菠菜", "油菜", "空心菜", "苋菜", "茼蒿", "油麦菜", "生菜", "苦菊", "芹菜", "香菜", "韭菜", "红苋菜", "木耳菜", "水芹", "茭白", "紫苏", "笋", "鱼腥草", "马齿苋", "香椿",
    ]),
  },
  {
    id: "fruiting-vegetables",
    name: "果菜",
    ingredients: ingredientList("fruiting-vegetables", [
      "西红柿", "茄子", "青椒", "彩椒", "辣椒", "黄瓜", "南瓜", "冬瓜", "苦瓜", "丝瓜", "秋葵", "西葫芦", "长豆角", "荷兰豆", "四季豆", "扁豆", "豌豆", "毛豆", "蚕豆", "绿豆",
    ]),
  },
  {
    id: "flowers-mushrooms",
    name: "花菜和菌菇",
    ingredients: ingredientList("flowers-mushrooms", ["花菜", "西兰花", "黄花菜", "蘑菇", "香菇", "金针菇", "平菇", "杏鲍菇", "木耳"]),
  },
  {
    id: "alliums",
    name: "葱蒜类",
    ingredients: ingredientList("alliums", ["洋葱", "蒜苗", "大葱", "小葱", "大蒜", "大蒜叶"]),
  },
  {
    id: "soy-products",
    name: "豆制品",
    ingredients: ingredientList("soy-products", ["豆腐", "千张", "腐竹", "豆干", "油豆腐", "素鸡", "魔芋"]),
  },
  {
    id: "staples",
    name: "主食及加工品",
    ingredients: ingredientList("staples", ["面条", "米粉", "米线", "粉丝", "年糕", "馒头", "包子", "饺子", "馄饨", "油条", "烧麦", "螺蛳粉"]),
  },
  {
    id: "fruit",
    name: "水果",
    ingredients: ingredientList("fruit", [
      "苹果", "梨", "香蕉", "橙子", "橘子", "柚子", "柠檬", "西瓜", "哈密瓜", "甜瓜", "猕猴桃", "葡萄", "提子", "草莓", "蓝莓", "火龙果", "芒果", "木瓜", "桃子", "李子", "杏", "樱桃", "榴莲", "菠萝蜜", "牛油果", "百香果", "山竹", "菠萝", "柿子", "杨梅", "桑葚", "荔枝", "龙眼",
    ]),
  },
];

export const PICKY_INGREDIENTS: readonly PickyIngredient[] = PICKY_CATEGORIES.flatMap(({ ingredients }) => ingredients);

export const PICKY_SCORE_PROFILES: readonly PickyScoreProfile[] = [
  {
    id: "open-table",
    name: "来者不拒",
    kind: "score",
    minScore: 0,
    maxScore: 5,
    tags: ["好养活", "开放型", "随缘吃"],
    description: "大多数食材都愿意尝试，点菜时很好商量。",
  },
  {
    id: "small-preferences",
    name: "小有坚持",
    kind: "score",
    minScore: 6,
    maxScore: 15,
    tags: ["轻挑食", "有偏好", "好商量"],
    description: "有几样明确不爱吃，其余选择依然很宽。",
  },
  {
    id: "clear-boundaries",
    name: "边界清晰",
    kind: "score",
    minScore: 16,
    maxScore: 30,
    tags: ["选择型", "有原则", "先看菜单"],
    description: "知道自己的口味边界，提前看菜单最安心。",
  },
  {
    id: "selective-taster",
    name: "挑食鉴赏家",
    kind: "score",
    minScore: 31,
    maxScore: 100,
    tags: ["高标准", "谨慎派", "熟悉食材"],
    description: "可选范围很具体，熟悉的搭配更容易满意。",
  },
];

export const PICKY_PATTERN_PROFILES: readonly PickyPatternProfile[] = [
  {
    id: "red-meat-detour",
    name: "红肉绕行者",
    kind: "pattern",
    patternId: "red-meat",
    focusLabel: "畜肉类",
    categoryIds: ["livestock"],
    tags: ["畜肉", "清淡派", "另选主菜"],
    description: "遇到畜肉会主动绕开，其他主菜更合胃口。",
  },
  {
    id: "poultry-pause",
    name: "禽类暂停键",
    kind: "pattern",
    patternId: "poultry",
    focusLabel: "家禽肉",
    categoryIds: ["poultry"],
    tags: ["家禽", "口感敏感", "换种肉类"],
    description: "对家禽类兴趣不高，换一种主菜更自在。",
  },
  {
    id: "organ-offline",
    name: "内脏绝缘体",
    kind: "pattern",
    patternId: "organs",
    focusLabel: "动物内脏",
    categoryIds: ["livestock-organs", "poultry-organs"],
    tags: ["内脏", "气味敏感", "边界明确"],
    description: "动物内脏基本不在选择范围，边界十分明确。",
  },
  {
    id: "fish-detour",
    name: "鱼类绕行者",
    kind: "pattern",
    patternId: "fish",
    focusLabel: "鱼类",
    categoryIds: ["fish"],
    tags: ["鱼类", "怕刺", "水产谨慎"],
    description: "面对鱼类会格外谨慎，无刺选择更容易接受。",
  },
  {
    id: "crustacean-do-not-disturb",
    name: "虾蟹免打扰",
    kind: "pattern",
    patternId: "crustaceans",
    focusLabel: "虾蟹类",
    categoryIds: ["crustaceans"],
    tags: ["虾蟹", "甲壳类", "海鲜回避"],
    description: "虾蟹不是心头好，海鲜局会先确认菜单。",
  },
  {
    id: "shellfish-no-order",
    name: "贝壳不接单",
    kind: "pattern",
    patternId: "shellfish",
    focusLabel: "螺贝类",
    categoryIds: ["shellfish"],
    tags: ["贝类", "口感敏感", "海味谨慎"],
    description: "对螺贝类的海味和口感都有自己的标准。",
  },
  {
    id: "soft-seafood-retreat",
    name: "软体水产退避",
    kind: "pattern",
    patternId: "soft-seafood",
    focusLabel: "软体水产",
    categoryIds: ["soft-seafood"],
    tags: ["软体", "弹滑口感", "水产回避"],
    description: "弹滑口感不太合拍，软体水产通常会跳过。",
  },
  {
    id: "river-food-observer",
    name: "河鲜观察员",
    kind: "pattern",
    patternId: "freshwater",
    focusLabel: "两栖和河鲜",
    categoryIds: ["freshwater"],
    tags: ["河鲜", "两栖", "尝鲜谨慎"],
    description: "面对河鲜和两栖食材，会先观察再决定。",
  },
  {
    id: "egg-day-off",
    name: "鸡蛋请假员",
    kind: "pattern",
    patternId: "eggs",
    focusLabel: "蛋类",
    categoryIds: ["eggs"],
    tags: ["蛋类", "蛋味敏感", "替代蛋白"],
    description: "蛋类经常缺席，其他蛋白质更受欢迎。",
  },
  {
    id: "leafy-escape",
    name: "绿叶菜逃兵",
    kind: "pattern",
    patternId: "leafy-vegetables",
    focusLabel: "叶菜",
    categoryIds: ["leafy-vegetables"],
    tags: ["叶菜", "绿色警报", "口感优先"],
    description: "绿叶菜出现时会认真筛选，口感决定去留。",
  },
  {
    id: "fruit-veg-curator",
    name: "果菜选择家",
    kind: "pattern",
    patternId: "fruiting-vegetables",
    focusLabel: "果菜",
    categoryIds: ["fruiting-vegetables"],
    tags: ["茄瓜类", "精挑细选", "蔬菜偏好"],
    description: "茄瓜豆各有标准，熟悉的做法更容易过关。",
  },
  {
    id: "mushroom-detour",
    name: "菌菇绕道派",
    kind: "pattern",
    patternId: "flowers-mushrooms",
    focusLabel: "花菜和菌菇",
    categoryIds: ["flowers-mushrooms"],
    tags: ["菌菇", "菌味敏感", "另配蔬菜"],
    description: "菌菇风味不太合拍，换份蔬菜更稳妥。",
  },
  {
    id: "allium-invisible",
    name: "葱蒜隐身派",
    kind: "pattern",
    patternId: "alliums",
    focusLabel: "葱蒜类",
    categoryIds: ["alliums"],
    tags: ["葱蒜", "气味敏感", "清口偏好"],
    description: "更偏爱清爽味道，葱蒜最好少一点。",
  },
  {
    id: "soy-pause",
    name: "豆制品暂停",
    kind: "pattern",
    patternId: "soy-products",
    focusLabel: "豆制品",
    categoryIds: ["soy-products"],
    tags: ["豆味敏感", "替代蛋白", "口感优先"],
    description: "豆制品不是首选，会更偏爱其他蛋白质。",
  },
  {
    id: "carb-moderation",
    name: "碳水节制派",
    kind: "pattern",
    patternId: "staples",
    focusLabel: "主食及加工品",
    categoryIds: ["staples"],
    tags: ["主食", "面点", "少碳偏好"],
    description: "主食选择很具体，更重视整桌菜的搭配。",
  },
  {
    id: "fruit-goes-with-the-flow",
    name: "水果随缘派",
    kind: "pattern",
    patternId: "fruit",
    focusLabel: "水果",
    categoryIds: ["fruit"],
    tags: ["水果", "酸甜偏好", "选择性尝鲜"],
    description: "水果各有缘分，酸甜和口感都要刚刚好。",
  },
];

export const PICKY_PROFILES: readonly PickyProfile[] = [...PICKY_SCORE_PROFILES, ...PICKY_PATTERN_PROFILES];

const VALID_INGREDIENT_IDS = new Set(PICKY_INGREDIENTS.map(({ id }) => id));
const VALID_CATEGORY_IDS = new Set<PickyCategoryId>(PICKY_CATEGORIES.map(({ id }) => id));
const CATEGORY_BY_ID = new Map(PICKY_CATEGORIES.map((category) => [category.id, category]));

function uniqueValidIngredientIds(ids: Iterable<string>): string[] {
  return [...new Set(ids)].filter((id) => VALID_INGREDIENT_IDS.has(id));
}

export function ingredientIdsForCategory(categoryId: PickyCategoryId): readonly string[] {
  return CATEGORY_BY_ID.get(categoryId)?.ingredients.map(({ id }) => id) ?? [];
}

export function calculatePickyIndex(selectedIds: Iterable<string>): number {
  const selectedCount = uniqueValidIngredientIds(selectedIds).length;
  return Math.round((selectedCount / PICKY_INGREDIENTS.length) * 100);
}

export function pickyScoreProfile(index: number): PickyScoreProfile {
  const safeIndex = Math.min(100, Math.max(0, Math.round(index)));
  return PICKY_SCORE_PROFILES.find(({ minScore, maxScore }) => safeIndex >= minScore && safeIndex <= maxScore)
    ?? PICKY_SCORE_PROFILES[0]!;
}

export function scorePickyPatterns(selectedIds: Iterable<string>): PickyPatternScore[] {
  const selected = new Set(uniqueValidIngredientIds(selectedIds));
  return PICKY_PATTERN_PROFILES.map((profile) => {
    const patternIds = profile.categoryIds.flatMap((categoryId) => ingredientIdsForCategory(categoryId));
    const selectedCount = patternIds.filter((id) => selected.has(id)).length;
    return {
      profile,
      selectedCount,
      totalCount: patternIds.length,
      ratio: patternIds.length === 0 ? 0 : selectedCount / patternIds.length,
    };
  }).sort((left, right) =>
    right.ratio - left.ratio
      || right.selectedCount - left.selectedCount
      || PICKY_PATTERN_PROFILES.indexOf(left.profile) - PICKY_PATTERN_PROFILES.indexOf(right.profile));
}

export function getPickyResult(selectedIds: Iterable<string>): PickyResult {
  const selected = uniqueValidIngredientIds(selectedIds);
  const index = calculatePickyIndex(selected);
  const patternScores = scorePickyPatterns(selected);
  const primaryPattern = patternScores.find(({ selectedCount }) => selectedCount > 0) ?? null;
  const matchedPattern = patternScores.find(({ ratio, selectedCount }) =>
    ratio >= PICKY_PATTERN_THRESHOLD && selectedCount >= PICKY_PATTERN_MIN_SELECTIONS);

  return {
    index,
    selectedCount: selected.length,
    totalCount: PICKY_INGREDIENTS.length,
    profile: matchedPattern?.profile ?? pickyScoreProfile(index),
    primaryPattern,
  };
}

export function emptyPickyTestDraft(): PickyTestDraft {
  return {
    version: PICKY_TEST_VERSION,
    selectedIngredientIds: [],
    expandedCategoryIds: [PICKY_CATEGORIES[0]!.id],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function parsePickyTestDraft(raw: string | null): PickyTestDraft {
  if (!raw) return emptyPickyTestDraft();
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)
      || value.version !== PICKY_TEST_VERSION
      || !Array.isArray(value.selectedIngredientIds)
      || !Array.isArray(value.expandedCategoryIds)) {
      return emptyPickyTestDraft();
    }
    return {
      version: PICKY_TEST_VERSION,
      selectedIngredientIds: uniqueValidIngredientIds(
        value.selectedIngredientIds.filter((id): id is string => typeof id === "string"),
      ),
      expandedCategoryIds: [...new Set(
        value.expandedCategoryIds.filter(
          (id): id is PickyCategoryId => typeof id === "string" && VALID_CATEGORY_IDS.has(id as PickyCategoryId),
        ),
      )],
    };
  } catch {
    return emptyPickyTestDraft();
  }
}

export function serializePickyTestDraft(draft: PickyTestDraft): string {
  return JSON.stringify({
    version: PICKY_TEST_VERSION,
    selectedIngredientIds: uniqueValidIngredientIds(draft.selectedIngredientIds),
    expandedCategoryIds: [...new Set(draft.expandedCategoryIds)].filter((id) => VALID_CATEGORY_IDS.has(id)),
  });
}
